import type { AISuggestedRiskControlsResult } from 'src/pages/controls/tab/suggested-risk-controls/useAISuggestedRiskControlsStore';
import { useAISuggestedRiskControlsStore } from 'src/pages/controls/tab/suggested-risk-controls/useAISuggestedRiskControlsStore';
import { v4 as uuidV4 } from 'uuid';

import type { AIWorkflowJobResult } from '@/components/ai-workflows/aiWorkflowStore';
import { buildAiWorkflowService } from '@/components/ai-workflows/useAIWorkflowsService';

// This must match the options defined on the AI Workflow API
type SuggestionMode = 'clean-run' | 'complement-existing';

interface SuggestControlsBody {
  riskId: string;
  suggestionMode: SuggestionMode;
  matchAgainstControlLibrary: boolean;
  libraryMatchThreshold: number;
  additionalContext: string | null;
}

export interface AISuggestedRiskControlsService {
  runWorkflow(riskId: string): Promise<void>;
}

const settingsSessionStorageKey = 'AI-Suggest-Controls-Settings';

function applyDefaultsForMissingControlValues(
  result: AIWorkflowJobResult<AISuggestedRiskControlsResult> | null | undefined
): void {
  if (result?.result?.suggestedControls) {
    result.result.suggestedControls = result.result.suggestedControls.map(
      (ctrl) => {
        const { controlId, createdByUser, createdAtTimestamp, ...rest } = ctrl;

        return {
          ...rest,
          controlId: controlId ?? uuidV4(),
          createdByUser: createdByUser ?? 'AI',
          createdAtTimestamp: createdAtTimestamp ?? new Date().toISOString(),
        };
      }
    );
  }
}

function buildSuggestControlsBody(riskId: string): () => SuggestControlsBody {
  ////////
  // Temporary code to allow the product team to play around with the configuration
  // by adjusting values in session storage
  let settings = sessionStorage.getItem(settingsSessionStorageKey);

  if (!settings) {
    settings = JSON.stringify({
      suggestionMode: 'clean-run',
      matchAgainstControlLibrary: true,
      libraryMatchThreshold: 0.75,
      additionalContext: null,
    });

    sessionStorage.setItem(settingsSessionStorageKey, settings);
  }

  const controlSettings = JSON.parse(settings);
  ////////

  return () => Object.assign({ riskId }, controlSettings);
}

export function useAISuggestedRiskControlsService(
  retryInterval: number = 3_000
): AISuggestedRiskControlsService {
  const { runWorkflow } = buildAiWorkflowService<AISuggestedRiskControlsResult>(
    useAISuggestedRiskControlsStore,
    'suggest-controls-by-risk-id',
    retryInterval
  )();

  return {
    runWorkflow: async (riskId: string) => {
      await runWorkflow(
        buildSuggestControlsBody(riskId),
        applyDefaultsForMissingControlValues
      );
    },
  };
}
